import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  addAdminWalletApi,
  AddAdminWalletRequest,
  AddAdminWalletResponse,
  addAdminWalletsForWithdrawalsApi,
  AddAdminWithdrawalWalletsRequest,
  AdminBalancesResponse,
  AdminWallets,
  deleteAdminWalletsForWithdrawalsApi,
  DeleteAdminWithdrawalWalletsRequest,
  getAdminBalancesApi,
  getAdminWalletApi,
  getAdminWalletsForWithdrawalsApi,
  getTradeBalanceApi,
  TradeBalanceRequest,
  TradeBalanceResponse,
} from "./api";
import { State } from "@features/base/types";

export const getAdminBalances = createAsyncThunk<
  AdminBalancesResponse,
  undefined
>("adminBalances/getAdminBalances", async (_, { rejectWithValue }) => {
  try {
    return await getAdminBalancesApi();
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const getTradeBalance = createAsyncThunk<
  TradeBalanceResponse,
  { data?: TradeBalanceRequest }
>("adminBalances/getTradeBalance", async ({ data }, { rejectWithValue }) => {
  try {
    return await getTradeBalanceApi(data);
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const addAdminWallet = createAsyncThunk<
  AddAdminWalletResponse,
  { data: AddAdminWalletRequest }
>("adminBalances/addAdminWallet", async ({ data }, { rejectWithValue }) => {
  try {
    return await addAdminWalletApi(data);
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const getAdminWallet = createAsyncThunk<
  string[],
  unknown
>("adminBalances/getAdminWallet", async (_, { rejectWithValue }) => {
  try {
    return await getAdminWalletApi();
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const getAdminWalletsForWithdrawals = createAsyncThunk<
  AdminWallets,
  undefined
>(
  "adminBalances/getAdminWalletsForWithdrawals",
  async (_, { rejectWithValue }) => {
    try {
      return await getAdminWalletsForWithdrawalsApi();
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const addAdminWalletsForWithdrawals = createAsyncThunk<
  string,
  { data: AddAdminWithdrawalWalletsRequest }
>(
  "adminBalances/addAdminWalletsForWithdrawals",
  async ({ data }, { rejectWithValue }) => {
    try {
      return await addAdminWalletsForWithdrawalsApi(data);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
export const deleteAdminWalletsForWithdrawals = createAsyncThunk<
  string,
  { data: DeleteAdminWithdrawalWalletsRequest }
>(
  "adminBalances/deleteAdminWalletsForWithdrawals",
  async ({ data }, { rejectWithValue }) => {
    try {
      return await deleteAdminWalletsForWithdrawalsApi(data);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

type AdminBalancesState = State<{
  balances: AdminBalancesResponse | null;
  tradeBalance: TradeBalanceResponse | null;
  tradeBalanceLoading: boolean;
  tradeBalanceError: string | null;
  withdrawalWallets: AdminWallets | null;
  wallets: AdminWallets | null;
  walletsLoading: boolean;
  isModalLoading: boolean;
}>;

const initialState: AdminBalancesState = {
  balances: null,
  tradeBalance: null,
  loading: false,
  tradeBalanceLoading: false,
  error: null,
  tradeBalanceError: null,
  withdrawalWallets: null,
  walletsLoading: false,
  wallets: null,
  isModalLoading: false,
};

const adminBalancesSlice = createSlice({
  name: "adminBalances",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAdminBalances.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAdminBalances.fulfilled, (state, action) => {
        state.loading = false;
        state.balances = action.payload;
      })
      .addCase(getAdminBalances.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getTradeBalance.pending, (state) => {
        state.tradeBalanceLoading = true;
        state.tradeBalanceError = null;
      })
      .addCase(getTradeBalance.fulfilled, (state, action) => {
        state.tradeBalanceLoading = false;
        state.tradeBalance = action.payload;
      })
      .addCase(getTradeBalance.rejected, (state, action) => {
        state.tradeBalanceLoading = false;
        state.tradeBalanceError = action.payload as string;
      })
      .addCase(addAdminWallet.pending, (state) => {
        state.isModalLoading = true;
      })
      .addCase(addAdminWallet.fulfilled, (state, action) => {
        state.isModalLoading = false;
        console.log(action.payload);
      })
      .addCase(addAdminWallet.rejected, (state, action) => {
        state.isModalLoading = false;
        state.error = action.payload as string;
      })
      .addCase(getAdminWalletsForWithdrawals.pending, (state) => {
        state.walletsLoading = true;
      })
      .addCase(getAdminWalletsForWithdrawals.fulfilled, (state, action) => {
        state.walletsLoading = false;
        state.withdrawalWallets = action.payload;
      })
      .addCase(getAdminWalletsForWithdrawals.rejected, (state, action) => {
        state.walletsLoading = false;
        state.error = action.payload as string;
        state.withdrawalWallets = [];
      })
      .addCase(getAdminWallet.pending, (state) => {
        state.walletsLoading = true;
      })
      .addCase(getAdminWallet.fulfilled, (state, action) => {
        state.walletsLoading = false;
        state.wallets = action.payload;
      })
      .addCase(getAdminWallet.rejected, (state, action) => {
        state.walletsLoading = false;
        state.error = action.payload as string;
        state.wallets = [];
      })
      .addCase(addAdminWalletsForWithdrawals.pending, (state) => {
        state.isModalLoading = true;
      })
      .addCase(addAdminWalletsForWithdrawals.fulfilled, (state, action) => {
        state.isModalLoading = false;
        console.log(action.payload);
      })
      .addCase(addAdminWalletsForWithdrawals.rejected, (state, action) => {
        state.isModalLoading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteAdminWalletsForWithdrawals.pending, (state) => {
        state.walletsLoading = true;
      })
      .addCase(deleteAdminWalletsForWithdrawals.fulfilled, (state, action) => {
        state.walletsLoading = false;
        console.log(action.payload);
      })
      .addCase(deleteAdminWalletsForWithdrawals.rejected, (state, action) => {
        state.walletsLoading = false;
        state.error = action.payload as string;
      });
  },
});

export default adminBalancesSlice.reducer;
